'use client';

import { useEffect, useRef } from 'react';

import { cn } from '@/lib/utils';
import { SkeletonTextBlock } from '@/shared/ui/primitives/skeletons/Skeletons';

export interface StreamingToolTextProps {
  text: string | undefined;
  isStreaming: boolean;
  height?: number;
  className?: string;
}

const DEFAULT_HEIGHT = 200;

export function StreamingToolText({
  text,
  isStreaming,
  height = DEFAULT_HEIGHT,
  className,
}: StreamingToolTextProps) {
  const hasText = typeof text === 'string' && text.length > 0;
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isStreaming || !scrollRef.current) {
      return;
    }
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [text, isStreaming]);

  return (
    <div
      className={cn(
        'not-prose w-full overflow-hidden rounded-lg border',
        isStreaming ? 'border-primary/30' : 'border-border',
        className
      )}
    >
      <div className="relative" style={{ height }}>
        <div ref={scrollRef} className="overflow-y-auto p-4" style={{ height }}>
          {hasText ? (
            <p className="whitespace-pre-wrap text-[11px] leading-relaxed text-muted-foreground">
              {text}
            </p>
          ) : (
            <SkeletonTextBlock lines={4} className="opacity-20 [&_div]:h-2" />
          )}
        </div>

        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-background to-transparent"
          aria-hidden="true"
        />
      </div>
    </div>
  );
}
